import React, { useState } from "react";
import PageTopBar from "../../../../components/common/PageTopBar";
import { toolbarButtonSx } from "../../../../components/common/toolbarStyles";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useAcademicYears } from "../../hooks/useAcademicYears";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { IconButton, Menu, MenuItem } from "@mui/material";
import { AcademicYearStatus } from "../../types";
import { useNotify } from "../../../../context/NotificationContext";

/** `tabs`: when shown inside Academics → Setup, the tabs replace the title in the top bar. */
const AcadmicYearsUI: React.FC<{ tabs?: React.ReactNode }> = ({ tabs }) => {
  const notify = useNotify();
  const { academicYears, loading, creating, creatingPrevious, error, hasPlanned, hasActive, actions, results, initializationResult,
    clearInitializationResult } = useAcademicYears();

  const [previousDialogOpen, setPreviousDialogOpen] = useState(false);

  // List is newest first, so the oldest year is the last one. Name pattern matches the backend: 2023-24
  const oldestYear = academicYears.length > 0 ? academicYears[academicYears.length - 1] : null;
  const previousStartYear = oldestYear ? parseInt(oldestYear.name.substring(0, 4), 10) - 1 : null;
  const previousYearName = previousStartYear !== null ? `${previousStartYear}-${(previousStartYear % 100) + 1}` : "";

  const handleCreatePrevious = async () => {
    try {
      const created = await actions.createPrevious();
      notify(`Academic year ${created.name} created as CLOSED.`);
      setPreviousDialogOpen(false);
    } catch (err: any) {
      // Backend sends the exception message as a plain string body
      const data = err?.response?.data;
      const message = typeof data === "string" ? data : data?.message;
      notify(message || "Failed to create previous academic year.", "error");
    }
  };

  // state to track which row's menu is open
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; id: string } | null>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, id: string) => {
    setMenuAnchor({ el: event.currentTarget, id });
  };
  const handleMenuClose = () => setMenuAnchor(null);

  const handleInitialize = (name: string) => {
    actions.init(name);
  }
  const handleActivate = (id: string) => {
    alert("This feature is in development phase");
  }
  const handleClose = (id: string) => { }
  const handleDelete = (id: string) => { }
  const sync = async () => {
    const data = await actions.sync();
  }

  /**
      * Academic Year State Machine
      *
      * States and Allowed Actions
      *
      * PLANNED
      *   - Initialize
      *   - Delete
      *
      * INITIALIZED
      *   - Activate (only if no ACTIVE academic year exists)
      *
      * ACTIVE
      *   - Close
      *
      * CLOSED
      *   - No actions available
      *
      * Business Rules
      *   - Only one ACTIVE academic year can exist at a time.
      *   - Only one PLANNED academic year can exist at a time.
      *   - Only one INITIALIZED academic year can exist at a time.
      *   - Multiple CLOSED academic years are allowed.
      *
      * Lifecycle
      *   PLANNED -> INITIALIZED -> ACTIVE -> CLOSED
      *
      * Notes
      *   - An academic year can be initialized even if another academic year is ACTIVE.
      *   - Activation is allowed only when there is no existing ACTIVE academic year.
*/

  return (
    <Box>
      <PageTopBar
        left={tabs ?? "Academic Years"}
        right={
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              sx={toolbarButtonSx}
              startIcon={<AddIcon />}
              onClick={() => setPreviousDialogOpen(true)}
              disabled={!oldestYear || creatingPrevious}
            >
              Add Previous Year
            </Button>
            <Button
              variant="contained"
              size="small"
              sx={toolbarButtonSx}
              startIcon={creating ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
              onClick={actions.create}
              disabled={hasPlanned}
            >
              {creating ? "Creating..." : "Add Year"}
            </Button>
          </Box>
        }
      />

      <Dialog open={previousDialogOpen} onClose={() => !creatingPrevious && setPreviousDialogOpen(false)}>
        <DialogTitle>Add Previous Academic Year</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Create <b>{previousYearName}</b> as a CLOSED academic year?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviousDialogOpen(false)} disabled={creatingPrevious}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreatePrevious}
            disabled={creatingPrevious}
            startIcon={creatingPrevious ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {creatingPrevious ? "Creating..." : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: "grey.50" }}>
              <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Start Date</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>End Date</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : academicYears.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    No academic years yet.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              academicYears.map((academicYear, index) => (
                <TableRow
                  key={academicYear.id}
                  hover
                  sx={{ "&:last-child td": { border: 0 } }}
                >
                  <TableCell>{academicYears.length - index}</TableCell>
                  <TableCell>{academicYear.name}</TableCell>
                  <TableCell>{academicYear.startDate}</TableCell>
                  <TableCell>{academicYear.endDate}</TableCell>
                  <TableCell>
                    <Chip
                      label={academicYear.status}
                      size="small"
                      color={academicYear.status === AcademicYearStatus.ACTIVE ? "success" : "default"}
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={(e) => handleMenuOpen(e, academicYear.id)}>
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box>
        {/* Syn status */}
        <Dialog
          open={results.syncDialogOpen}
          onClose={actions.closeSyncDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Sync Results</DialogTitle>
          <DialogContent>
            {(results.academicYearSyncResult
              && (
                (results.academicYearSyncResult.activated
                  && results.academicYearSyncResult.activated.length > 0)
                || (results.academicYearSyncResult.failed
                  && results.academicYearSyncResult.failed.length > 0)
              ))
              ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: "grey.50" }}>
                        <TableCell sx={{ fontWeight: 600 }}>Admission No</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Grade</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Section</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {results.academicYearSyncResult.activated &&
                        results.academicYearSyncResult.activated.map((ele, index) => (
                          <TableRow key={`a-${index}`} hover>
                            <TableCell>{ele.admissionNo}</TableCell>
                            <TableCell>{ele.name}</TableCell>
                            <TableCell>{ele.grade}</TableCell>
                            <TableCell>{ele.section}</TableCell>
                            <TableCell>
                              <Chip label="Activated" size="small" color="success" />
                            </TableCell>
                          </TableRow>
                        ))}
                      {results.academicYearSyncResult.failed &&
                        results.academicYearSyncResult.failed.map((ele, index) => (
                          <TableRow key={`f-${index}`} hover>
                            <TableCell>{ele.admissionNo}</TableCell>
                            <TableCell>{ele.name}</TableCell>
                            <TableCell>{ele.grade}</TableCell>
                            <TableCell>{ele.section}</TableCell>
                            <TableCell>
                              <Chip label="Failed" size="small" color="error" />
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <>No active students without enrollments.</>
              )}
          </DialogContent>
          <DialogActions>
            <Button onClick={actions.closeSyncDialog}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>

      <Menu
        anchorEl={menuAnchor?.el}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        {(() => {
          const row = academicYears.find((ay) => ay.id === menuAnchor?.id);
          if (!row) return null;

          if (row.status === AcademicYearStatus.PLANNED) return [
            <MenuItem key="planned-init"
              onClick={() => { handleInitialize(row.name); handleMenuClose(); }}
              disabled={hasActive}
            >
              Initialize
            </MenuItem>,
            <MenuItem key="to-delete" onClick={() => { handleDelete(row.name); handleMenuClose(); }} sx={{ color: "error.main" }}>
              Delete
            </MenuItem>,
          ];

          if (row.status === AcademicYearStatus.INITIALIZED) return (
            <MenuItem key="init-activate"
              disabled={hasActive}
              onClick={() => { handleActivate(row.name); handleMenuClose(); }}>
              Activate
            </MenuItem>
          );

          if (row.status === AcademicYearStatus.ACTIVE) return (
            <>
              <MenuItem key="sync-active" onClick={() => { sync(); handleMenuClose(); }}>
                Sync
              </MenuItem>
              <MenuItem key="active-to-close" onClick={() => { handleClose(row.name); handleMenuClose(); }}>
                Close
              </MenuItem>
            </>
          );

          <MenuItem key="none" onClick={() => { handleClose(row.name); handleMenuClose(); }}>
            None
          </MenuItem>
        })()}
      </Menu>

      {initializationResult && (
        <Alert
          severity={initializationResult.failed > 0 ? "warning" : "success"}
          onClose={clearInitializationResult}
          sx={{ mt: 2 }}
        >
          Enrollments — Created: {initializationResult.created}, Skipped: {initializationResult.skipped}, Failed: {initializationResult.failed}
        </Alert>
      )}
    </Box>
  );
};

export default AcadmicYearsUI;
